import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import Button from 'Components/UI/Button';
import useMobileLayout from 'Hooks/useMobileLayout';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import fr from 'facet-rest';
import { noop } from 'Helpers/PropTypes';
import { relationshipType } from 'facet-rest/src/consts/relationships';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';

import {
  root,
  section,
  title,
  links as linkContainer,
  link as linkButton,
  descriptionContainer,
  descriptionText,
  mobile,
  descriptionTextMobile,
  linkMobile, linkButtonWrapper,
} from '../sectionClasses';

const useStyles = buildUseStyles('Default', (theme) => ({
  // Main Classes
  root: root(theme),
  section: section(theme),
  title: title(theme),
  descriptionContainer: descriptionContainer(theme),
  descriptionText: descriptionText(theme),
  linkContainer: linkContainer(theme),
  linkButton: linkButton(theme),
  linkButtonWrapper: linkButtonWrapper(theme),
  // Mobile Classes
  mobile: mobile(theme),
  descriptionTextMobile: descriptionTextMobile(theme),
  linkMobile: linkMobile(theme),
}));

const LinkButton = ({
  className,
  link,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const { loadPageContentLink } = usePageContentContext();
  const { loadModalCollectionByLink } = useModalContext();

  const href = link.meta('type') === relationshipType.hyperlink ? link.link('related').url() : null;
  const onClick = link.meta('type') === relationshipType.pagelink ? () => {
    if (link.meta('modal') === 'true') {
      loadModalCollectionByLink(link.link('related').url());
    } else {
      loadPageContentLink(link.link('related'));
    }
  } : noop;

  return (
    <div className={classNames(className, classes.linkButtonWrapper)}>
      <Button
        className={classNames(className, classes.linkButton, { [classes.linkMobile]: isMobile })}
        color={'primary'}
        onClick={onClick}
        href={href}
        target={typeof href === 'string' && href.startsWith('mailto:') ? undefined : '_blank'}
        rel={'noopener noreferrer'}
        test={`profile-page-section-${link.attr('name')}`}
      >
        {link.meta('prompt')}
      </Button>
    </div>
  );
};

LinkButton.propTypes = {
  className: PropTypes.string,
  link: PropTypes.instanceOf(fr.Link).isRequired,
};

LinkButton.defaultProps = {
  className: '',
};

const Default = ({
  className,
  title: titleText,
  description,
  links,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();

  return (
    <Box className={classNames(classes.root, className, { [classes.mobile]: isMobile })}>
      <Box className={classes.section}>
        <Typography
          className={classes.title}
          variant={'h6'}
          color={'black'}
        >
          {titleText}
        </Typography>
      </Box>
      {(!isMobile || description) && (
        <Box
          className={classNames(classes.section, classes.descriptionContainer, { [classes.descriptionContainerMobile]: isMobile })}
        >
          <Typography
            className={classNames(classes.descriptionText, { [classes.descriptionTextMobile]: isMobile })}
            variant={'h6'}
          >
            {description}
          </Typography>
        </Box>
      )}
      <Box className={classNames(classes.section, classes.linkContainer)}>
        {links.map(link => (
          <LinkButton
            link={link}
            color={'primary'}
            key={`${link.attr('name')}-link`}
          />
        ))}
      </Box>
    </Box>
  );
};

Default.defaultProps = {
  className: '',
  title: '',
  description: '',
  links: [],
};

Default.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  links: PropTypes.arrayOf(PropTypes.instanceOf(fr.Link)),
};

export default Default;
